import {observer} from 'mobx-react';
import 'moment/locale/pt-br';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {AdminContext} from '../../apiContexts';
import NavVertical from '../../components/navVertical';
import useStore from '../../hooks/use-store';
import {logOut} from '../../services/authService';
import NavBar from '../../views/admin/NavBar';
import EventContainer from '../event-container';
import {AdminGlobalStyle, AdminStyle} from '../../views/admin/styles';
import ModalEnum from '../../views/modals/modalEnum';
import history from '../../routes/history';
import PropTypes from 'prop-types';
import useServices from '../../hooks/use-services';
import useDevices from '../../hooks/use-devices';
import useCustomers from '../../hooks/use-customers';
import useUsers from '../../hooks/use-users';
import {useSubjectsRealtimeUpdates} from 'src/hooks/use-subjets-realtime-updates.js';
import {isCurrentProfileMonitor, getCurrentProfile} from 'src/helpers/profile';

const AdminContainer = (props) => {
  const {
    children,
  } = props;

  const intl = useIntl();
  const {driverService} = useServices();
  const {store} = useStore();
  const {devices: devicesStore} = useDevices();
  const {users} = useUsers();
  const {customers} = useCustomers({all: true});
  useEffect(() => {
    driverService.list().then((drivers) => {
      store.drivers.setDrivers(drivers);
    });
  }, []);

  // A busca do topo filtra por IMEI, nome, grupo, cliente e CPF/CNPJ -- tudo cadastro, e o proprio
  // modelo ja resolve cliente e documento por referencia. Antes isso era refeito num efeito que
  // serializava a colecao inteira e varria a lista de clientes por dispositivo, com `devices.list`
  // (instantaneo profundo) na lista de dependencias: qualquer posicao recebida disparava tudo de
  // novo, varias vezes por segundo, em toda tela do painel.
  const devicesCustomer = devicesStore.registrationList;

  useSubjectsRealtimeUpdates();

  const links = useMemo(
    () => [
      {
          label: intl.formatMessage({id: 'mapTitle'}),
          icon: 'fad fa-map-marked-alt',
          type: ModalEnum.MAP,
          fn: () => history.push('/admin/devices'),
      },
      {
          label: intl.formatMessage({id: 'sharedSinister'}),
          icon: 'fad fa-shield',
          type: ModalEnum.SINISTER,
          fn: () => history.push('/admin/manage/sinister'),
      },
      {
          label: intl.formatMessage({id: 'sharedFinancial'}),
          icon: 'fad fa-file-invoice-dollar',
          type: ModalEnum.BILLING,
          fn: () => history.push('/admin/manage/billing'),
      },
      {
          label: intl.formatMessage({id: 'sharedCustomers'}),
          icon: 'fad fa-book-user',
          type: ModalEnum.CUSTOMERS,
          fn: () => history.push('/admin/manage/customers'),
      },
      {
          label: intl.formatMessage({id: 'sharedDevice'}),
          icon: 'fad fa-car',
          type: ModalEnum.DEVICE,
          fn: () => history.push('/admin/manage/device'),
      },
      {
          label: intl.formatMessage({id: 'settingsUsers'}),
          icon: 'fad fa-users',
          type: ModalEnum.USERS,
          fn: () => history.push('/admin/manage/users'),
      },
      {
          label: intl.formatMessage({id: 'groupDialog'}),
          icon: 'fad fa-object-group',
          type: ModalEnum.GROUP,
          fn: () => history.push('/admin/manage/groups'),
      },
      {
          label: intl.formatMessage({id: 'sharedGeofence'}),
          icon: 'fad fa-location-circle',
          type: ModalEnum.GEOFENCE,
          fn: () => history.push('/admin/manage/geofences'),
      },
      {
          label: intl.formatMessage({id: 'sharedDriver'}),
          icon: 'fad fa-steering-wheel',
          type: ModalEnum.DRIVER,
          fn: () => history.push('/admin/manage/drivers'),
      },
      {
          label: intl.formatMessage({id: 'sharedNotification'}),
          icon: 'fad fa-envelope-open-text',
          type: ModalEnum.NOTIFICATION,
          fn: () => history.push('/admin/manage/notifications'),
      },
      {
          label: intl.formatMessage({id: 'sharedCalendar'}),
          icon: 'fad fa-calendar-alt',
          type: ModalEnum.CALENDAR,
          fn: () => history.push('/admin/manage/calendars'),
      },
      {
          label: intl.formatMessage({id: 'sharedMaintenance'}),
          icon: 'fad fa-tools',
          type: ModalEnum.MAINTENANCE,
          fn: () => history.push('/admin/manage/maintenance'),
      },
      {
        label: intl.formatMessage({id: 'sharedIntegration'}),
        icon: 'fad fa-layer-group',
        type: ModalEnum.INTEGRATION,
        fn: () => history.push('/admin/manage/integrations'),
      },
      {
        label: intl.formatMessage({id: 'sharedIntegrationConfig'}),
        icon: 'fad fa-code-merge',
        type: ModalEnum.INTEGRATIONCONFIG,
        fn: () => history.push('/admin/manage/integrationconfig'),
      },
      {
        label: intl.formatMessage({id: 'sharedReportJobs'}),
        icon: 'fad fa-tasks',
        type: ModalEnum.REPORT_JOBS,
        fn: () => history.push('/admin/manage/reportjobs'),
      },
      {
        label: intl.formatMessage({id: 'settingsServer'}),
        icon: 'fad fa-server',
        type: ModalEnum.SERVER,
        fn: () => history.push('/admin/manage/server'),
      },
      // {
      //     label: intl.formatMessage({id: 'settingsUser'}),
      //     icon: 'fad fa-user-tie',
      //     type: ModalEnum.ACCOUNT,
      //     fn: () => history.push('/admin/manage/account'),
      // },
      {
          label: intl.formatMessage({id: 'sharedCompany'}),
          icon: 'fad fa-building',
          type: ModalEnum.COMPANY_CONFIG,
          fn: () => history.push('/admin/manage/company'),
      },
      {
          label: intl.formatMessage({id: 'sharedLogout'}),
          icon: 'fad fa-door-open',
          fn: logOut,
      },
    ],
    [],
  );

  const filterLinks = useCallback(() => {
    if (getCurrentProfile() === 'admin') {
      return links;
    }

    if (customers.isCurrentCustomerMonitoring || isCurrentProfileMonitor()) {
      const isLinkAllowed = (link) => {
        if (link.type === ModalEnum.SERVER ||
            link.type === ModalEnum.INTEGRATION ||
            link.type === ModalEnum.COMPANY_CONFIG
        ) return false;

        return true;
      };

      return links.filter(isLinkAllowed);
    }

    return links;
  }, [links]);

  return (
    <>
      <AdminGlobalStyle/>
      <AdminStyle>
        <AdminContext.Provider>
          <NavBar devices={devicesCustomer} users={users} customers={customers}/>
          <EventContainer/>
          <NavVertical links={filterLinks()}/>
          <div id="children">{children}</div>
        </AdminContext.Provider>
      </AdminStyle>
    </>
  );
};

export default observer(AdminContainer);

AdminContainer.propTypes = {
  children: PropTypes.any,
};
